import React from 'react';
import { DndContext, closestCorners } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, ClickAwayListener, Grid } from '@mui/material';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Draggable } from 'sharedComponents/DragAndDrop';
import { useBoardNodeCRUD } from 'hooks/common/useBoardNodeCRUD';
import BoardNodeContainer from 'components/common/SparkOutputBoard/BoardNodeContainer';
import { InfoCard } from 'sharedComponents/UI';
import BoardNodeStepConnectionWrapper from 'components/common/SparkOutputBoard/helper/BoardNodeStepConnectionWrapper';
import { useReferencePopup } from 'components/common/SparkOutputBoard/helper/useReferencePopup';
import StepReferencePopup from 'components/StepReferencePopup';
import { useBoardNodeTypes } from 'hooks/EntityTypes/useBoardNodeTypes';
import type { BoardNodes } from 'types/BoardNode';

type BoardListViewContainerProps = {
  boardID: string | null | undefined;
  boardNodes?: BoardNodes[];
};

function SparkOutputBoardListViewContainer({
  boardID,
  boardNodes,
}: BoardListViewContainerProps) {
  const { handleDragEnd, sortableItems, handleUpdateNode, handleDeleteNode } =
    useBoardNodeCRUD({
      boardID,
      boardNodes,
    });
  const { idMap: boardNodeTypesIdMap } = useBoardNodeTypes();
  const {
    anchor,
    insertTextAtCursor,
    isPopOverOver,
    getLastFocusedElement,
    closePopover,
  } = useReferencePopup({});

  const getNodeType = React.useCallback(
    (boardNodeTypeID: string | number) => {
      return boardNodeTypesIdMap?.[boardNodeTypeID];
    },
    [boardNodeTypesIdMap]
  );

  const _handleUpdateNode = React.useCallback(
    (node: BoardNodes) => {
      handleUpdateNode({ id: node.id, ...node.attributes });
    },
    [handleUpdateNode]
  );

  if (!boardNodes?.length) {
    return (
      <InfoCard
        cardStyles={{ margin: 2, width: '100%' }}
        info='No output'
        type='warning'
      />
    );
  }

  return (
    <DndContext
      collisionDetection={closestCorners}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <Grid container borderRadius='10px'>
        <SortableContext
          strategy={verticalListSortingStrategy}
          items={sortableItems}
        >
          <Grid
            container
            style={{ width: '100%', height: '100%', position: 'relative' }}
            gap={2}
          >
            {boardNodes?.map((boardNode) => (
              <Draggable
                key={boardNode.id}
                id={boardNode.id}
                render={({ isHoveringOver }) => (
                  <BoardNodeStepConnectionWrapper
                    sparkStepID={boardNode.attributes.sparkStepID}
                    readOnly={false}
                    isSparkOutputPreview={false}
                    render={({ isLinked, onError, isError }) => (
                      <>
                        <ClickAwayListener onClickAway={closePopover}>
                          <BoardNodeContainer
                            boardNode={boardNode}
                            nodeType={getNodeType(
                              boardNode.attributes.boardNodeTypeID
                            )}
                            getLastFocusedElement={getLastFocusedElement}
                            readOnly={false}
                            isCanvasView={false}
                            isHover={isHoveringOver}
                            shouldResolveReference={false}
                            onUpdateNode={_handleUpdateNode}
                            onDeleteNode={handleDeleteNode}
                            isError={isError}
                            onError={onError}
                            isLinked={isLinked}
                          />
                        </ClickAwayListener>
                        {isPopOverOver && !isLinked && (
                          <Box
                            sx={{
                              '& .MuiPaper-root': {
                                minWidth: '30rem',
                                borderRadius: '10px',
                                userSelect: 'none',
                              },
                            }}
                          >
                            <StepReferencePopup
                              onClose={closePopover}
                              handleTagClick={(reference) =>
                                insertTextAtCursor(
                                  reference,
                                  getNodeType(
                                    boardNode.attributes.boardNodeTypeID
                                  )?.attributes.key || ''
                                )
                              }
                              anchorEl={anchor}
                              placement='left-start'
                            />
                          </Box>
                        )}
                      </>
                    )}
                  />
                )}
              />
            ))}
          </Grid>
        </SortableContext>
      </Grid>
    </DndContext>
  );
}
export default React.memo(SparkOutputBoardListViewContainer);
